import React, { useState } from "react";
// Assuming Input component is defined elsewhere
import Input from "./Input";
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { confirmSignUp, resendCode, signIn } from '../services/cognito';
import { crmService } from '../services/api';

export default function Confirm() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState('');

  const [success, setSuccess] = useState('');

  const confirm = async () => {

    if (!code) {
      setError('Please enter the verification code.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await confirmSignUp(email, code);

      // TODO: Move Brevo sync to backend (security issue - API key exposed)
      // For now, commenting out to remove security vulnerability
      // Backend should handle Brevo integration after user confirmation

      // Sync to Brevo - Calling backend endpoint
      const syncToBrevo = async () => {
        try {
          const pendingBrevo = sessionStorage.getItem('pending_brevo_sync');
          if (pendingBrevo) {
            const contactData = JSON.parse(pendingBrevo);

            // Backend endpoint checks public access or we can rely on it being public as per lambda.js
            console.log('[Confirm] Syncing to Brevo:', contactData.email);

            // Using crmService which now uses landlordApi (could attach auth, but backend might be public).
            // If backend is public, auth header is ignored.
            // Ideally we should make this a safe call.
            await crmService.createContact(contactData);

            console.log('[Confirm] Brevo sync successful');
            sessionStorage.removeItem('pending_brevo_sync');
          }
        } catch (error) {
          console.error('[Brevo] Sync failed:', error);
          // Don't block flow on CRM error
        }
      };
      await syncToBrevo();

      // Auto-login if we have credentials
      const tempAuth = sessionStorage.getItem('temp_auth');
      if (tempAuth) {
        const { email: savedEmail, password } = JSON.parse(tempAuth);
        if (savedEmail === email) {
          const session: any = await signIn(email, password);
          // Store tokens for API calls
          localStorage.setItem('idToken', session.getIdToken().getJwtToken());
          localStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
          sessionStorage.removeItem('temp_auth');
          navigate('/dashboard');
          window.location.reload(); // Refresh to update auth state in App.tsx
          return;
        }
      }

      setError('');
      setSuccess('Account confirmed! Please log in.'); // Use success state or error field for neutral msg
      setTimeout(() => navigate('/login'), 2000);

    } catch (err: any) {
      setError(err.message);
    }
  };

  const resend = async () => {
    setError('');
    setSuccess('');
    try {
      const result = await resendCode(email);
      if (result.success) {
        setSuccess("Verification code resent to your email.");
      } else {
        setError(result.error || "Failed to resend code.");
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    // Outer container for centering
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center sm:py-12">
      <div className="p-10 xs:p-0 mx-auto md:w-full md:max-w-md">
        {/* Logo/Image section */}
        <div className="flex justify-center items-center py-6">
          <Link to="/">
            <img src="/Landlord-Pro-Logo.png" className="h-20 w-auto" alt="Landlord Pro Logo" />
          </Link>
        </div>

        {/* Main Card */}
        <div className="bg-white shadow w-full rounded-lg divide-y divide-gray-200">
          <div className="px-5 py-7">
            {/* Confirmation Header */}
            <h2 className="text-center text-2xl font-bold text-gray-700 mb-4">
              Confirm Your Account
            </h2>
            {/* Email Display */}
            <p className="text-center text-sm text-gray-500 mb-4 truncate">
              Email: <span className="font-semibold text-gray-800">{email}</span>
            </p>

            {/* Spam Folder Warning */}
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 flex items-center">
                <span className="mr-2">⚠️</span>
                Can't find the code? Check your spam or junk folder.
              </p>
            </div>

            {/* OTP Input Field */}
            <div className="mb-6">
              {/* NOTE: Assuming 'Input' component accepts a 'className' prop for full styling. 
                  If not, you'd need to style the actual input element inside 'Input.js' */}
              <Input
                label="OTP Code"
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {error && <p className='error mb-4 text-red-600 text-center'>{error}</p>}
            {success && <p className='success mb-4 text-green-600 text-center'>{success}</p>}
            <div className="flex flex-col space-y-4">
              {/* Confirm Button */}
              {/* Confirm Button */}
              <button
                onClick={confirm}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-150 ease-in-out"
              >
                Confirm
              </button>

              {/* Resend Code Button (Styled as a secondary/outline button) */}
              <button
                onClick={resend}
                className="bg-white border border-emerald-600 text-emerald-600 hover:bg-emerald-50 font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-150 ease-in-out"
              >
                Resend Code
              </button>
            </div>
          </div>
          {/* Removed the empty </div> inside the card */}
        </div>
      </div>
    </div>
  );
}